import { useSearchParams } from "@solidjs/router";
import { AiOutlineUser } from "solid-icons/ai";
import { BiRegularLink, BiSolidMessage } from "solid-icons/bi";
import {
  type Component,
  createMemo,
  createSignal,
  For,
  onMount,
  Show,
} from "solid-js";
import { Dynamic } from "solid-js/web";
import { useI18n } from "@/api/i18n";
import { Container } from "@/components/shared/Container";
import { DocumentCard } from "@/components/shared/documents/document-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGetUserApplications } from "@/domains/application/api";
import {
  APPLICANT_VISIBLE_STATUS,
  getApplicationStatusBadge,
} from "@/domains/application/constant/status";
import type { JobApplication } from "@/domains/application/types";
import type { JobApplicationStatus } from "@/domains/company/company/api/types";
import { useAssetDownload } from "@/domains/documents/hooks/use-asset-download";
import { categoryLabel } from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { useGetProtectedJobOfferV2 } from "@/domains/job-offer/api";
import { OfferSubcategoryChips } from "@/domains/job-offer/components/offer-subcategory-chips";
import { REMOTE_ICONS } from "@/domains/job-offer/constants/remote-icons";
import type { Effort } from "@/domains/job-offer/create/constants";
import type { JobOfferPopulatedWithCompanyAndPostedBy } from "@/domains/job-offer/types";
import { getLastResumeAsset, getSortedAssetsByType } from "@/libs/assets";
import { formatDeadline } from "@/libs/date";
import {
  calculatePercentageBreakdown,
  formatCurrency,
  formatDailyRate,
  formatJobCompensation,
} from "@/libs/format";

const APPLICATION_TAB_ALL = "all" as const;

type ApplicationTabValue =
  | typeof APPLICATION_TAB_ALL
  | (typeof APPLICANT_VISIBLE_STATUS)[number];

const APPLICATION_TAB_VALUES: ApplicationTabValue[] = [
  APPLICATION_TAB_ALL,
  ...APPLICANT_VISIBLE_STATUS,
];

function getApplicationTabParam(
  raw: string | string[] | undefined,
): string | undefined {
  if (raw == null) return undefined;
  return Array.isArray(raw) ? raw[0] : raw;
}

function isValidApplicationTab(
  value: string | undefined,
): value is ApplicationTabValue {
  if (value === APPLICATION_TAB_ALL) return true;
  return APPLICANT_VISIBLE_STATUS.includes(
    value as (typeof APPLICANT_VISIBLE_STATUS)[number],
  );
}

const ApplicationsEmptyFallback: Component<{
  status?: ApplicationTabValue;
}> = (props) => {
  const { t } = useI18n();
  const title = () =>
    props.status
      ? t("profile.tabs.applications.empty.for_status.title")
      : t("profile.tabs.applications.empty.title");
  const description = () =>
    props.status
      ? t("profile.tabs.applications.empty.for_status.description")
      : t("profile.tabs.applications.empty.description");
  return (
    <div class="text-center p-12 space-y-4">
      <div class="text-6xl mb-4">📋</div>
      <div class="text-gray-600 text-lg font-medium">{title()}</div>
      <div class="text-gray-500 text-sm">{description()}</div>
      <Button variant="outline" size="sm" as="a" href="/">
        {t("profile.tabs.applications.empty.button")}
      </Button>
    </div>
  );
};

export const ApplicationsTab: Component = () => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const applications = useGetUserApplications();

  const currentApplicationTab = createMemo((): ApplicationTabValue => {
    const raw = getApplicationTabParam(searchParams.application_tab);
    return isValidApplicationTab(raw) ? raw : APPLICATION_TAB_ALL;
  });

  const onApplicationTabChange = (value: ApplicationTabValue) => {
    setSearchParams({ application_tab: value });
  };

  onMount(() => {
    const raw = getApplicationTabParam(searchParams.application_tab);
    if (!isValidApplicationTab(raw)) {
      setSearchParams({ application_tab: APPLICATION_TAB_ALL });
    }
  });

  // Combine applications with job offers
  const applicationsWithJobOffers = createMemo(() => {
    if (!applications.data?.job_application || !applications.data?.job_offers) {
      return [];
    }

    return applications.data.job_application
      .map((application) => {
        const jobOffer = applications.data.job_offers.find(
          (job) => job.id === application.job_offer_id,
        );
        return { application, jobOffer };
      })
      .filter((item): item is ApplicationWithJobOffer => !!item.jobOffer);
  });

  const groupedApplications = createMemo(() => {
    const list = applicationsWithJobOffers();
    const byStatus = {} as Record<
      JobApplicationStatus,
      ApplicationWithJobOffer[]
    >;
    for (const status of APPLICANT_VISIBLE_STATUS) {
      byStatus[status] = list.filter(
        (item) => (item.application.status as JobApplicationStatus) === status,
      );
    }
    return {
      all: list,
      byStatus,
    };
  });

  return (
    <Container size="full">
      <div class="flex flex-col gap-6">
        <div class="flex items-center justify-between">
          <h1 class="text-2xl font-bold text-gray-900">
            {t("profile.tabs.applications.title")}
          </h1>
        </div>

        <Show when={applications.isLoading}>
          <div class="flex items-center justify-center p-8">
            <div class="text-gray-600 text-lg">{t("common.loading")}</div>
          </div>
        </Show>

        <Show when={applications.isError}>
          <div class="flex items-center justify-center p-8">
            <div class="text-red-600 text-center">
              <div class="text-lg font-medium mb-2">
                Errore nel caricamento delle candidature
              </div>
              <div class="text-sm">
                {applications.error?.message || "Riprova più tardi"}
              </div>
            </div>
          </div>
        </Show>

        <Show when={applications.data}>
          <Show
            when={groupedApplications().all.length > 0}
            fallback={<ApplicationsEmptyFallback />}
          >
            <Tabs
              value={currentApplicationTab()}
              onChange={(value) =>
                onApplicationTabChange(value as ApplicationTabValue)
              }
              class="w-full"
            >
              <TabsList class="overflow-x-auto flex flex-nowrap gap-1 border-0 p-0">
                <For each={APPLICATION_TAB_VALUES}>
                  {(tabValue) => (
                    <TabsTrigger
                      value={tabValue}
                      class="shrink-0 max-w-[70%] md:shrink"
                    >
                      {tabValue === APPLICATION_TAB_ALL
                        ? t("profile.tabs.applications.status_tabs.all")
                        : t(
                            `profile.tabs.applications.status_tabs.${tabValue}`,
                          )}
                    </TabsTrigger>
                  )}
                </For>
              </TabsList>

              <TabsContent value={APPLICATION_TAB_ALL} class="mt-4">
                <div class="flex flex-col gap-4">
                  <For each={groupedApplications().all}>
                    {(item) => (
                      <ApplicationCard
                        application={item.application}
                        jobOffer={item.jobOffer}
                      />
                    )}
                  </For>
                </div>
              </TabsContent>

              <For each={APPLICANT_VISIBLE_STATUS}>
                {(status) => (
                  <TabsContent value={status} class="mt-4">
                    <Show
                      when={groupedApplications().byStatus[status].length > 0}
                      fallback={<ApplicationsEmptyFallback status={status} />}
                    >
                      <div class="flex flex-col gap-4">
                        <For each={groupedApplications().byStatus[status]}>
                          {(item) => (
                            <ApplicationCard
                              application={item.application}
                              jobOffer={item.jobOffer}
                            />
                          )}
                        </For>
                      </div>
                    </Show>
                  </TabsContent>
                )}
              </For>
            </Tabs>
          </Show>
        </Show>
      </div>
    </Container>
  );
};

interface ApplicationWithJobOffer {
  application: JobApplication;
  jobOffer: JobOfferPopulatedWithCompanyAndPostedBy;
}

type AdditionalInformation = {
  weekly_availability?: Effort;
  timezone_hours?: string;
};

const ApplicationCard: Component<{
  application: JobApplication;
  jobOffer: JobOfferPopulatedWithCompanyAndPostedBy;
}> = (props) => {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = createSignal(false);

  // Fetch protected job offer data for sensitive fields
  const protectedJobOffer = useGetProtectedJobOfferV2(
    props.jobOffer.id,
    "completed",
  );

  // Merge protected data with public job offer data
  const mergedJobOffer = createMemo(() => {
    const protectedData = protectedJobOffer.data?.job_offer;
    if (!protectedData) return props.jobOffer;
    const basePostedBy = props.jobOffer.posted_by;
    return {
      ...props.jobOffer,
      calendar_url: protectedData.calendar_url,
      apply_email_address: protectedData.apply_email_address,
      posted_by: {
        id: basePostedBy.id,
        avatar_url: basePostedBy.avatar_url,
        full_name: basePostedBy.full_name,
        phone_number: protectedData.posted_by.phone_number,
      },
    };
  });

  const hasCoverLetter = () =>
    !!props.application.cover_letter &&
    props.application.cover_letter.trim().length > 0;

  const additionalInformation = createMemo(() => {
    return props.application.additional_information as AdditionalInformation;
  });

  const badgeStatus = createMemo(() => {
    return getApplicationStatusBadge(
      props.application.status as JobApplicationStatus,
    );
  });

  const upfrontPaymentBreakdown = createMemo(() => {
    if (!props.application.payment_details?.upfront) return null;
    if (props.application.payment_details?.upfront.upfront_percentage === 0)
      return null;
    return calculatePercentageBreakdown(
      props.application.budget ?? 0,
      props.application.payment_details?.upfront?.upfront_percentage ?? 0,
    );
  });

  const { download, isPending } = useAssetDownload();

  const uploadedResumeAsset = createMemo(() => {
    return getLastResumeAsset(props.application?.assets ?? []);
  });

  const additionalDocuments = createMemo(() => {
    return getSortedAssetsByType(
      props.application?.assets ?? [],
      "APPLICATION_ATTACHMENT",
    );
  });

  return (
    <Card class="max-w-full bg-white rounded-sm w-full border-collapse relative lg:mx-auto shadow-none">
      <CardHeader class="p-4">
        <div class="flex items-center justify-between flex-row gap-4">
          <div class="flex items-start lg:items-center gap-2 w-full">
            <div class="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
              <Show
                when={props.jobOffer.company.logo_url}
                fallback={<AiOutlineUser />}
              >
                <img
                  src={props.jobOffer.company.logo_url!}
                  alt={props.jobOffer.title}
                  class="w-full h-full object-cover rounded-full"
                />
              </Show>
            </div>
            <div class="flex items-baseline gap-4 w-full">
              <div>
                <CardTitle class="text-lg no-wrap whitespace-nowrap">
                  {props.jobOffer.title}
                </CardTitle>
                <CardDescription class="text-xs text-gray-500">
                  {props.jobOffer.company.name}
                </CardDescription>
              </div>
              <div class="flex items-center gap-2 no-wrap">
                <Badge variant={badgeStatus().variant}>
                  {badgeStatus().text}
                </Badge>
                <Show when={props.application.contacted_at}>
                  <Badge
                    variant="outline"
                    class="text-green-700 bg-green-50 border-green-200 px-3 py-1 whitespace-nowrap"
                  >
                    ✅ Contattato il{" "}
                    {formatDeadline(props.application.contacted_at!)}
                  </Badge>
                </Show>
              </div>
            </div>
          </div>
          <div class="flex items-center justify-end gap-2 w-full">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded())}
            >
              {isExpanded() ? "Nascondi Dettagli" : "Mostra Dettagli"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent class="px-4 pb-4">
        <div class="flex flex-col gap-2">
          <div class="flex flex-col gap-2">
            <div class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Informazioni principali
            </div>
            <div class="flex items-center gap-1 flex-wrap">
              <Show when={mergedJobOffer().payment_type}>
                {(pt) => (
                  <Badge variant="info" size="sm">
                    {t(`joblist.jobOffer.paymentType.${pt()}`)}:{" "}
                    {formatJobCompensation(mergedJobOffer())}
                  </Badge>
                )}
              </Show>

              <Badge variant="light-primary" size="sm">
                {categoryLabel(mergedJobOffer().job_category)}
              </Badge>
              <OfferSubcategoryChips
                category={mergedJobOffer().job_category}
                keys={mergedJobOffer().job_subcategory}
              />
            </div>
            <div class="flex flex-wrap items-center gap-2 w-full">
              <Show when={mergedJobOffer().duration}>
                <Badge variant="secondary" size="sm">
                  {t(`joblist.jobOffer.duration.${mergedJobOffer().duration!}`)}
                </Badge>
              </Show>
              <Show when={mergedJobOffer().effort}>
                <Badge variant="secondary" size="sm">
                  {t(`joblist.jobOffer.effort.${mergedJobOffer().effort!}`)}
                </Badge>
              </Show>
              <Show when={mergedJobOffer().remote_policy}>
                {(remotePolicy) => (
                  <>
                    <Badge variant="secondary" size="sm">
                      <Dynamic
                        component={REMOTE_ICONS[remotePolicy()]}
                        size={14}
                        class="mr-1"
                      />
                      {t(`common.remote_policy_options.${remotePolicy()}`)}
                    </Badge>
                    <Show when={remotePolicy() !== "remote"}>
                      <Badge variant="secondary" size="sm">
                        {mergedJobOffer().location?.join(", ")}
                      </Badge>
                    </Show>
                  </>
                )}
              </Show>
              <Show
                when={
                  props.jobOffer.renewable !== undefined ||
                  props.jobOffer.flexible !== undefined
                }
              >
                <div class="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <Show when={mergedJobOffer().renewable !== undefined}>
                    <Badge
                      variant={
                        mergedJobOffer().renewable ? "light-green" : "secondary"
                      }
                      size="sm"
                      class={
                        mergedJobOffer().renewable
                          ? "text-green-700 bg-green-50 border-green-100"
                          : ""
                      }
                    >
                      {mergedJobOffer().renewable
                        ? t("common.renewable")
                        : t("common.non_renewable")}
                    </Badge>
                  </Show>
                  <Show when={mergedJobOffer().flexible !== undefined}>
                    <Badge
                      variant={
                        mergedJobOffer().flexible
                          ? "light-primary"
                          : "secondary"
                      }
                      size="sm"
                      class={
                        mergedJobOffer().flexible
                          ? "text-primary bg-primary/10 border-primary/20"
                          : ""
                      }
                    >
                      {mergedJobOffer().flexible
                        ? t("common.flexible")
                        : t("common.non_flexible")}
                    </Badge>
                  </Show>
                </div>
              </Show>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Expanded Details Section */}
      <Show when={isExpanded()}>
        <div class="px-4 pb-4">
          <div class="border-t pt-4">
            <div class="space-y-4">
              {/* Application Details */}
              <Card class="rounded-lg shadow-none">
                <CardHeader>
                  <CardTitle class="text-lg font-semibold mb-3 text-gray-800">
                    {t("profile.tabs.applications.details.title")}
                  </CardTitle>
                </CardHeader>

                <CardContent class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p class="text-sm text-gray-600 mb-1">
                      {t("profile.tabs.applications.details.status")}
                    </p>
                    <Badge variant={badgeStatus().variant}>
                      {badgeStatus().text}
                    </Badge>
                  </div>
                  <div>
                    <p class="text-sm text-gray-600 mb-1">
                      {t("profile.tabs.applications.details.createdAt")}
                    </p>
                    <p class="text-sm font-medium">
                      {new Date(
                        props.application.created_at,
                      ).toLocaleDateString("it-IT")}
                    </p>
                  </div>
                  <Show when={additionalInformation()}>
                    <Show when={additionalInformation().weekly_availability}>
                      <div>
                        <p class="text-sm text-gray-600 mb-1">
                          {t(
                            "profile.tabs.applications.details.weeklyAvailability",
                          )}
                        </p>
                        <p class="text-sm font-medium">
                          {t(
                            `common.effort_options.${additionalInformation().weekly_availability!}`,
                          )}
                        </p>
                      </div>
                    </Show>
                    <Show when={additionalInformation().timezone_hours}>
                      <div>
                        <p class="text-sm text-gray-600 mb-1">
                          {t("profile.tabs.applications.details.timezoneHours")}
                        </p>
                        <p class="text-sm font-medium">
                          {additionalInformation().timezone_hours}
                        </p>
                      </div>
                    </Show>
                  </Show>
                  <Show when={props.application.budget}>
                    <div>
                      <p class="text-sm text-gray-600 mb-1">
                        {t("profile.tabs.applications.details.budget")}
                      </p>
                      <p class="text-sm font-medium">
                        €{props.application.budget}
                      </p>
                    </div>
                  </Show>
                  <Show when={props.application.daily_rate}>
                    <div>
                      <p class="text-sm text-gray-600 mb-1">
                        {t("profile.tabs.applications.details.dailyRate")}
                      </p>
                      <p class="text-sm font-medium">
                        {formatDailyRate(props.application.daily_rate)}
                      </p>
                    </div>
                  </Show>
                  <Show when={props.application.duration}>
                    <div>
                      <p class="text-sm text-gray-600 mb-1">
                        {t("profile.tabs.applications.details.duration")}
                      </p>
                      <p class="text-sm font-medium">
                        {t(
                          `joblist.jobOffer.duration.${props.application.duration!}`,
                        )}
                      </p>
                    </div>
                  </Show>
                  <Show when={props.application.effort}>
                    <div>
                      <p class="text-sm text-gray-600 mb-1">
                        {t("profile.tabs.applications.details.effort")}
                      </p>
                      <p class="text-sm font-medium">
                        {t(
                          `joblist.jobOffer.effort.${props.application.effort!}`,
                        )}
                      </p>
                    </div>
                  </Show>
                  <Show when={upfrontPaymentBreakdown()}>
                    <div>
                      <p class="text-sm text-gray-600 mb-1">
                        {t("profile.tabs.applications.details.upfrontPayment")}
                      </p>
                      <p class="text-sm font-medium">
                        {formatCurrency(
                          upfrontPaymentBreakdown()?.calculatedAmount ?? 0,
                        )}
                      </p>
                    </div>
                  </Show>
                  <Show when={mergedJobOffer().renewable !== undefined}>
                    <div>
                      <p class="text-sm text-gray-600 mb-1">
                        {t("common.renewable")}
                      </p>
                      <p class="text-sm font-medium">
                        {mergedJobOffer().renewable
                          ? t("common.renewable")
                          : t("common.non_renewable")}
                      </p>
                    </div>
                  </Show>
                  <Show when={mergedJobOffer().flexible !== undefined}>
                    <div>
                      <p class="text-sm text-gray-600 mb-1">
                        {t("common.flexible")}
                      </p>
                      <p class="text-sm font-medium">
                        {mergedJobOffer().flexible
                          ? t("common.flexible")
                          : t("common.non_flexible")}
                      </p>
                    </div>
                  </Show>
                </CardContent>
              </Card>

              {/* Cover Letter */}
              <Show when={hasCoverLetter()}>
                <Card class="rounded-lg shadow-none">
                  <CardHeader>
                    <CardTitle class="text-lg font-semibold">
                      {t("profile.tabs.applications.details.coverLetter")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p class="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                      {props.application.cover_letter}
                    </p>
                  </CardContent>
                </Card>
              </Show>

              <Show when={mergedJobOffer().calendar_url}>
                <Card class="rounded-lg shadow-none">
                  <CardHeader>
                    <CardTitle class="text-lg font-semibold">
                      {t("common.calendar_link_label")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <a
                      href={mergedJobOffer().calendar_url!}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center gap-2 hover:underline text-primary"
                    >
                      {mergedJobOffer().calendar_url}
                    </a>
                  </CardContent>
                </Card>
              </Show>
              {/* Informazioni Contatto Company */}
              <Show when={props.application.contacted_at}>
                <Card class="rounded-lg shadow-none">
                  <CardHeader>
                    <CardTitle class="text-lg font-semibold">
                      {t("profile.tabs.applications.details.contactCompany")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent class="grid grid-cols-2 gap-2">
                    <Show
                      when={
                        mergedJobOffer().internal_ats &&
                        mergedJobOffer().calendar_url
                      }
                    >
                      <a
                        href={mergedJobOffer().calendar_url!}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex items-center gap-2 hover:underline text-primary col-span-2"
                      >
                        📅 {t("common.calendar_link_label")}
                      </a>
                    </Show>
                    <a
                      href={props.jobOffer.company.website!}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center gap-2 hover:underline text-primary"
                    >
                      <BiRegularLink class="w-4 h-4" />
                      {props.jobOffer.company.website!}
                    </a>
                    <a
                      href={`mailto:${props.jobOffer.company.invoice_email!}?subject=${encodeURIComponent(`Interesse per la posizione: ${props.jobOffer.title}`)}&body=${encodeURIComponent(`Ciao,

sono interessato alla posizione di ${props.jobOffer.title} che avete pubblicato su SeVedemo. Vorrei saperne di più.

Cordiali saluti`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center gap-2 hover:underline text-primary"
                    >
                      <BiSolidMessage class="w-4 h-4" />
                      {props.jobOffer.company.invoice_email!}
                    </a>
                  </CardContent>
                </Card>
              </Show>

              {/* Resume Link */}
              <Show when={uploadedResumeAsset()}>
                {(asset) => (
                  <Card class="rounded-lg shadow-none">
                    <CardHeader>
                      <CardTitle class="text-lg font-semibold">
                        Curriculum Inviato
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Button
                        variant="neutral"
                        onClick={() => download(asset().id)}
                        disabled={isPending(asset().id)}
                        aria-busy={isPending(asset().id)}
                      >
                        📄 {t("common.download")} CV
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </Show>
              <Show when={additionalDocuments().length > 0}>
                <Card class="rounded-lg shadow-none">
                  <CardHeader>
                    <CardTitle class="text-lg font-semibold">
                      Documenti Aggiuntivi
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div class="flex flex-col gap-2">
                      <For each={additionalDocuments()}>
                        {(document) => (
                          <DocumentCard asset={document} size="small" />
                        )}
                      </For>
                    </div>
                  </CardContent>
                </Card>
              </Show>
            </div>
          </div>
        </div>
      </Show>
    </Card>
  );
};
